/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, func, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@material-ui/core';
import convertAlphaDecToHex from '../../../../../helpers/contentstack/convertAlphaDecToHex';
import ZipInput from '../../../../EnterpriseDesignSystem/Inputs/ZipInput/ZipInput';
import Select from '../../../../EnterpriseDesignSystem/Select/Select';
import StyledButton from '../../../../EnterpriseDesignSystem/Button/StyledButton';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import { getBrandName } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import GenericSkeleton from '../../../GraphqlSkeletonComponents/GenericSkeleton';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getCurrentPageType } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';

const GiftFinderEds = ({
    classes, data, handleSubmitEds, userSubmittedZip, handleChange, onSubmitCheck, zip, validatedZip, setSelectedOccasion, buttonStyle,
}) => {
    const giftFinderDecoupleEnabled = useSelector(getFeatureFlag('is-gift-finder-decouple-test-enabled'));
    const brandName = useSelector(getBrandName);
    const dispatch = useDispatch();
    const pageType = useSelector(getCurrentPageType);
    const giftFinderBgColor = data?.background_color || '';
    const giftFinderBgColorOpacity = data?.background_color_opacity || 1;
    const convertedGiftFinderBgColor = convertAlphaDecToHex(giftFinderBgColor, giftFinderBgColorOpacity);
    const giftFinderBodyTextColor = data?.text_color || '';
    const sectionTitle = data?.title_name || '';
    const edsZipLabel = data?.gift_finder_blocks?.[0]?.delivery_zip_code?.eds_label || 'Delivery zip code';
    const occasionTitle = data?.gift_finder_blocks?.[1]?.occasion?.title || '';
    const occasion_option_group = data?.gift_finder_blocks?.[1]?.occasion?.occasion_option_group || [];
    const giftFinderWidth = data?.has_full_width ? `${classes.giftFinderEdsInnerContainer} ${classes.giftFinderEdsInnerContainerFullWidth}` : classes.giftFinderEdsInnerContainer;
    const giftFinderWrapperClassName =  data?.has_negative_margin_top ? classes.giftFinderEdsFormWrapper : `${classes.giftFinderEdsFormWrapper} ${classes.giftFinderNoTopMargin}`;
    const isMobile = useMediaQuery('(max-width: 600px)');
    const { data: deCoupleGiftFinderData, loading, variables } = useSalesforcePersonalizedContentQuery({
        queryName: 'decupleGiftFinder',
        interactionName: `${brandName} - Get Campaign - Gift Finder Decouple`,
        featureFlag: giftFinderDecoupleEnabled && !isMobile,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    // Control or Test(variant) value from Decouple Gift Finder Sales Force Test
    const deCoupleGiftFinder = deCoupleGiftFinderData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;

    let giftFinderRender = true;
    if (typeof deCoupleGiftFinder !== 'undefined' && deCoupleGiftFinder) {
        const userGroup = deCoupleGiftFinder?.toLowerCase();
        const abTestingGroup = data?.a_b_testing?.variant?.toLowerCase();
        const isControlMatch = userGroup === 'control' && abTestingGroup === 'control';
        const isTestMatch = userGroup === 'test' && abTestingGroup === 'variant a';
        giftFinderRender = isControlMatch || isTestMatch;
    }
    useClickStreamCustomEventsTracking({
        salesforceResponse: deCoupleGiftFinderData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (typeof deCoupleGiftFinder !== 'undefined' && !window.wuDecoupleGiftFinderEventFired && deCoupleGiftFinder) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Gift Finder Decouple',
                eventLabel: deCoupleGiftFinder,
                feature_detail: deCoupleGiftFinder,
                feature_element: 'Gift Finder Decouple',
                feature_category: 'Experiment',
            };
            dispatch(trackEvent(eventParam));
            window.wuDeCoupleGiftFinderEventFired = true;
        }
        return () => {
            window.wuDeCoupleGiftFinderEventFired = false;
        };
    }, [deCoupleGiftFinder]);
    if (variables?.skip || loading) {
        return (
            <GenericSkeleton height="112px" width={giftFinderWidth} />
        );
    }

    return (
        giftFinderRender &&  (
            <form className={giftFinderWrapperClassName} onSubmit={handleSubmitEds}>
                <div className={giftFinderWidth} style={{ backgroundColor: convertedGiftFinderBgColor }}>
                    <div className={classes.giftFinderEdsSectionTitle} style={{ color: giftFinderBodyTextColor }}>
                        {sectionTitle || 'Find the Perfect Gift'}
                    </div>
                    <div className={classes.giftFinderEdsZipWidthContainer}>
                        <ZipInput
                            id="zipcode-native-label-placeholder"
                            dataTest="hp-zipcode-native-label-placeholder"
                            label={edsZipLabel}
                            name="zipcode"
                            type="text"
                            defaultValue={userSubmittedZip}
                            value={zip}
                            handleChange={(e) => {
                                handleChange('zip')(e);
                            }}
                            onSubmitCheck={onSubmitCheck}
                        />
                        {onSubmitCheck && (validatedZip !== zip)
                        && (
                            <div className={classes.zipInputError}>
                                Enter a valid ZIP code
                            </div>
                        )}
                    </div>
                    <div className={classes.giftFinderEdsSelectWidthContainer}>
                        <Select
                            label={occasionTitle}
                            id="occasion-native-label-placeholder"
                            dataTestId="4810-hp-occasion-native-label-placeholder"
                            name="occasion"
                            dropDownList={occasion_option_group}
                            handleChange={(e) => {
                                setSelectedOccasion(e.target.value);
                            }}
                        />
                    </div>
                    <div className={classes.giftFinderEdsButtonWidthContainer}>
                        <StyledButton
                            label={data?.cta_copy || 'Find a Gift Now'}
                            variant="light"
                            size="lg"
                            type="submit"
                            attributes={{
                                style: {
                                    border: `1px solid ${buttonStyle.backgroundColor}`,
                                    backgroundColor: buttonStyle.backgroundColor,
                                    color: buttonStyle.color,
                                    height: '56px',
                                    textTransform: 'none',
                                },
                                id: 'fagf-btn',
                                'data-test-id': 'fagf-btn',
                            }}
                        />
                    </div>
                </div>
            </form>
        )
    );
};

GiftFinderEds.propTypes = {
    buttonStyle: object,
    data: object.isRequired,
    classes: object.isRequired,
    onSubmitCheck: func.isRequired,
    handleChange: func.isRequired,
    handleSubmitEds: func.isRequired,
    validatedZip: string.isRequired,
    setSelectedOccasion: func.isRequired,
    userSubmittedZip: string.isRequired,
    zip: string,

};

GiftFinderEds.defaultProps = {
    zip: '',
    buttonStyle: {},

};

export default GiftFinderEds;
